/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useContext, useEffect, useRef } from 'react';
import produce from 'immer';
import { EditorContext } from '@/features/editor/context/editor-context';
import { useComplexState } from '@/utils/use-complex-state';
import { ElementTracking } from '@/webapi/use-experience-api';
import { QBType } from '@/components/query-builder/models';
import {
  createQBContext,
  QBContextProps,
} from '@/components/query-builder/context';
import { hashObject } from '@/components/hooks/use-cached-auto-save';

export interface TrackElementContext {
  name: string;
  description: string;
  code?: string;
  type: string;
  ordinal?: number;
  setType: (type: string) => void;
  focusInputX: number;
  focusInputY: number;
  qbContext: QBContextProps;
  setJsErrors(errors: any[]): void;
  setEventName: (name: string) => void;
  setDescription: (description: string) => void;
  setCode: (code: string) => void;
  jsCodeInvalid?: boolean;

  onSave(): void;

  setFocusedInput(x: number, y: number): void;
  hasChanges(): boolean;
}

const defaultContextValue: TrackElementContext = {
  name: ``,
  description: ``,
  code: ``,
  type: `rule`,
  jsCodeInvalid: false,
  setType: () => {},
  setEventName: () => {},
  setDescription: () => {},
  setCode: () => {},
  onSave: () => {},
  setJsErrors(_: any[]) {},
  hasChanges(): boolean {
    return false;
  },
  setFocusedInput(_: number, __: number) {},
  focusInputX: 0,
  focusInputY: 0,
  qbContext: undefined,
};

export const TrackElementContext =
  React.createContext<TrackElementContext>(defaultContextValue);

export function newTrackingElementContext({
  rules,
  name,
  description,
  type,
  code,
  ordinal,
  targetPlacement,
  targetDevice,
  variant,
}: ElementTracking): TrackElementContext {
  const {
    experienceState,
    devicePreview,
    cleanLastPickedElement,
    lastPickedElement,
    setShowElementTrackingPinkTip,
  } = useContext(EditorContext);

  const qbContext = createQBContext(rules, initialRules());
  const [state, setState] = useComplexState({
    individuaPickerClicked: false,
    name: name || ``,
    description: description || ``,
    ordinal,
    type: type || `rule`,
    code: code || ``,
    variant,
    targetDevice,
    targetPlacement,
    jsCodeInvalid: false,
    focusInputX: 0,
    focusInputY: 0,
  });

  useEffect(() => {
    cleanLastPickedElement();
    devicePreview.trackElementPicker();
  }, []);

  useEffect(() => {
    if (lastPickedElement?.selector) {
      qbContext.changeKey(
        state.focusInputX,
        produce(qbContext.queryState[state.focusInputX], (draft) => {
          const op =
            (lastPickedElement.kind as any) === `hover` ? `hover` : `click`;
          draft.qbProps.caption = op;
          draft.qbProps.envKey = op;
          draft.values.forEach((v, idx) => {
            v.op = op;
            if (idx === state.focusInputY) {
              v.value = lastPickedElement.selector;
            }
          });
        }),
      );
      devicePreview.trackElementPicker();
    }
  }, [lastPickedElement?.selector, state.individuaPickerClicked]);
  const initialStateHash = useRef<string>();
  useEffect(() => {
    initialStateHash.current = hashObject(state);
  }, []);
  const hasEmptySelector = !!qbContext?.queryState?.find((q) =>
    q.values?.find((v) => v.value === ``),
  );
  useEffect(() => {
    setShowElementTrackingPinkTip(hasEmptySelector);
  }, [hasEmptySelector]);

  return {
    ...state,
    hasChanges() {
      return hashObject(state) !== initialStateHash.current;
    },
    qbContext,
    setJsErrors(errors: any[]) {
      setState((s) => {
        s.jsCodeInvalid = errors?.length > 0;
      });
    },
    setFocusedInput(x: number, y: number) {
      cleanLastPickedElement();
      setState((s) => {
        s.individuaPickerClicked = true;
        s.focusInputY = y;
        s.focusInputX = x;
      });
      devicePreview.trackElementPicker();
    },
    setType(type: 'rule' | 'code') {
      setState((s) => {
        s.type = type;
      });
    },
    setEventName(name: string) {
      setState((s) => {
        s.name = name;
      });
    },
    setDescription(description: string) {
      setState((s) => {
        s.description = description?.toString();
      });
    },
    setCode(code: string) {
      setState((s) => {
        s.code = code;
      });
    },
    onSave() {
      experienceState.setElementTracking({
        type: state.type,
        rules: qbContext.queryState,
        code: state.code,
        name: state.name,
        description: state.description,
        ordinal: state.ordinal,
        variant: state.variant,
        targetPlacement: state.targetPlacement,
        targetDevice: state.targetDevice,
      });
    },
  };
}

function initialRules() {
  return [
    {
      kind: QBType.LIST_EDITABLE,
      caption: `action`,
      envKey: `click`,
      defaultOp: `click`,
      arrayValueOptions: [
        {
          key: `click`,
          value: `click`,
        },
        {
          key: `hover`,
          value: `hover`,
        },
      ],
    },
  ];
}
